// Allsson scroll-motion module. Plain JS, no deps, no framework.
// Three effects, all opt-in via data attributes, all disabled under
// prefers-reduced-motion:
//
//   data-fx="reveal"                 fade + rise + 6deg rotateX settle on enter
//   data-fx="stagger"                same, applied to children in sequence
//   data-fx="zoom"   data-fx-to      scale across the parent pin's progress
//   data-fx="parallax" data-fx-rate  translateY as a fraction of scroll
//   data-fx="fadeout"                copy fades + rises as its pin scrolls away
//   data-fx="tilt3d"  data-fx-deg    card rotates out of the page toward flat as
//                                    it travels up through the viewport
//
// A "pin" is any element with data-fx-pin: progress 0→1 is measured from the
// moment its top hits the viewport top to the moment its bottom does.
(() => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function initReveal(root) {
    const targets = [...root.querySelectorAll('[data-fx~="reveal"],[data-fx~="stagger"]')];
    targets.forEach((el) => {
      if (el.dataset.fx.includes("stagger")) {
        [...el.children].forEach((c, i) => {
          c.classList.add("fx-rise");
          c.style.setProperty("--fx-delay", `${Math.min(i * 90, 540)}ms`);
        });
      } else {
        el.classList.add("fx-rise");
      }
    });
    const all = [...root.querySelectorAll(".fx-rise")];
    const revealAll = () => all.forEach((el) => el.classList.add("is-in"));
    if (reduce) { revealAll(); return () => {}; }

    /* Rect-based rather than IntersectionObserver: this module also runs inside
       a transformed host (the design-system preview scales its content), where
       an implicit-root observer never reports an intersection and every target
       would stay at opacity 0 forever. The scrub below already proves rects are
       reliable here. */
    let raf = 0;
    let pending = all.slice();
    const check = () => {
      raf = 0;
      const vh = window.innerHeight;
      pending = pending.filter((el) => {
        const r = el.getBoundingClientRect();
        const visible = r.top < vh * 0.88 && r.bottom > 0;
        if (visible) el.classList.add("is-in");
        return !visible;
      });
      if (!pending.length) stop();
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    let failsafe = 0;
    const stop = () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearTimeout(failsafe);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    check();
    /* Terminal state must be visible: if anything is still hidden after a beat
       (a measurement that never resolves, a container that never scrolls), show
       it rather than leaving copy invisible. */
    failsafe = setTimeout(() => { revealAll(); stop(); }, 1600);
    return stop;
  }

  function initScrub(root) {
    if (reduce) return () => {};
    const pins = [...root.querySelectorAll("[data-fx-pin]")].map((pin) => ({
      pin,
      zoom: [...pin.querySelectorAll('[data-fx~="zoom"]')],
      fade: [...pin.querySelectorAll('[data-fx~="fadeout"]')],
    }));
    const plx = [...root.querySelectorAll('[data-fx~="parallax"]')];
    const tilt = [...root.querySelectorAll('[data-fx~="tilt3d"]')];
    tilt.forEach((el) => {
      el.style.transformStyle = "preserve-3d";
      if (el.parentElement) el.parentElement.style.perspective = el.dataset.fxPerspective || "1600px";
    });
    let raf = 0;
    const frame = () => {
      raf = 0;
      const vh = window.innerHeight;
      pins.forEach(({ pin, zoom, fade }) => {
        const r = pin.getBoundingClientRect();
        const span = Math.max(1, r.height - vh);
        const p = Math.min(1, Math.max(0, -r.top / span));
        zoom.forEach((el) => {
          const to = parseFloat(el.dataset.fxTo || "1.14");
          el.style.transform = `scale(${(1 + (to - 1) * p).toFixed(4)})`;
        });
        fade.forEach((el) => {
          const q = Math.min(1, p / 0.7);
          el.style.opacity = (1 - q).toFixed(3);
          el.style.transform = `translate3d(0,${(-56 * q).toFixed(1)}px,0)`;
        });
      });
      plx.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const rate = parseFloat(el.dataset.fxRate || "0.18");
        const mid = r.top + r.height / 2 - vh / 2;
        el.style.transform = `translate3d(0,${(-mid * rate).toFixed(1)}px,0)`;
      });
      tilt.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -300 || r.top > vh + 300) return;
        const deg = parseFloat(el.dataset.fxDeg || "9");
        // 1 when the element's top sits at the bottom of the viewport, 0 once its
        // centre reaches the middle of the screen — so it settles flat when read.
        const t = Math.min(1, Math.max(0, (r.top - vh * 0.18) / (vh * 0.82)));
        el.style.transform = `rotateX(${(deg * t).toFixed(2)}deg) scale(${(1 - 0.05 * t).toFixed(4)})`;
        el.style.transformOrigin = "50% 100%";
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(frame); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    frame();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }

  window.initScrollFx = function initScrollFx(root = document) {
    const stopReveal = initReveal(root);
    const stopScrub = initScrub(root);
    return () => { stopReveal(); stopScrub(); };
  };
})();
